import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
    FaTasks,
    FaEdit,
    FaTrash,
    FaPlus,
    FaRegCalendarCheck,
    FaCheckDouble,
    FaPenNib,
    FaHourglassHalf,
    FaChevronRight,
    FaTimes,
    FaCloudUploadAlt,
    FaQuestionCircle,
    FaClipboardList,
    FaArrowLeft,
    FaSave
} from 'react-icons/fa';
import DataTable from '../../SuperAdmin/shared/DataTable';
import Loader from '../../shared/Loader';
import EmptyState from '../../shared/EmptyState';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import { API_URL } from '../../../config';
import './TutorDashboardOverview.css';

const TutorAssignments = () => {
    const [activeTab, setActiveTab] = useState('roster'); // 'roster' or 'queue'
    const [assessments, setAssessments] = useState([]);
    const [pendingSubmissions, setPendingSubmissions] = useState([]);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isGradeModalOpen, setIsGradeModalOpen] = useState(false);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [editingAssessmentId, setEditingAssessmentId] = useState(null);

    // Filter States
    const [statusFilter, setStatusFilter] = useState('All');

    // Modal Step Management
    const [creationStep, setCreationStep] = useState(1);
    const [formData, setFormData] = useState({
        courseId: '',
        moduleId: '',
        title: '',
        instructions: '',
        contentType: 'Assignment',
        maxMarks: 100,
        passingPercentage: 40,
        dueDate: '',
        timeLimit: 30, // for exams
        questions: [], // for MCQ
        allowedFileTypes: ['.pdf', '.docx'],
        mediaFile: null
    });

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const [assRes, subRes, courseRes] = await Promise.all([
                axios.get('/api/tutor/assessments', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('/api/tutor/submissions/pending', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('/api/tutor/courses', { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setAssessments(assRes.data.filter(a => a.contentType !== 'Quiz'));
            setPendingSubmissions(subRes.data);
            setCourses(courseRes.data);
        } catch (err) {
            console.error(err);
            showError('Sync Failure', 'Could not retrieve assessment records.');
        } finally {
            setLoading(false);
        }
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        setFormData(prev => ({ ...prev, mediaFile: e.target.files[0] }));
    };

    // MCQ Builder Helpers
    const addQuestion = () => {
        setFormData(prev => ({
            ...prev,
            questions: [...prev.questions, { questionText: '', options: ['', '', '', ''], correctAnswer: '', marks: 5 }]
        }));
    };

    const updateQuestion = (qIdx, field, value) => {
        const updatedQuestions = [...formData.questions];
        updatedQuestions[qIdx][field] = value;
        setFormData(prev => ({ ...prev, questions: updatedQuestions }));
    };

    const updateOption = (qIdx, oIdx, value) => {
        const updatedQuestions = [...formData.questions];
        updatedQuestions[qIdx].options[oIdx] = value; // Keep literal for input, trim on sync or just trim here if we want clean DB
        setFormData(prev => ({ ...prev, questions: updatedQuestions }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        showLoading(editingAssessmentId ? 'Updating Assessment...' : 'Provisioning Assessment...');
        try {
            const token = localStorage.getItem('token');
            const data = new FormData();

            // Core data with normalization
            Object.keys(formData).forEach(key => {
                if (key === 'questions') {
                    const normalizedQuestions = formData.questions.map(q => ({
                        ...q,
                        questionText: q.questionText?.trim(),
                        options: q.options.map(opt => opt?.trim()),
                        correctAnswer: q.correctAnswer?.trim()
                    }));
                    data.append(key, JSON.stringify(normalizedQuestions));
                } else if (key === 'allowedFileTypes') {
                    data.append(key, JSON.stringify(formData[key]));
                } else if (key === 'mediaFile') {
                    if (formData[key]) data.append('mediaFile', formData[key]);
                } else {
                    data.append(key, formData[key]);
                }
            });

            // Sequence auto-calc (basic)
            if (!editingAssessmentId) {
                data.append('sequence', assessments.length + 1);
            }

            const url = editingAssessmentId
                ? `/api/tutor/courses/${formData.courseId}/modules/${formData.moduleId}/lessons/${editingAssessmentId}`
                : `/api/tutor/courses/${formData.courseId}/modules/${formData.moduleId}/lessons`;

            const method = editingAssessmentId ? 'put' : 'post';

            await axios[method](url, data, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            });

            showSuccess(editingAssessmentId ? 'Update Successful' : 'Deployment Successful', editingAssessmentId ? 'Assessment has been updated.' : 'New assessment has been broadcasted.');
            setIsCreateModalOpen(false);
            resetForm();
            fetchInitialData();
        } catch (err) {
            showError(editingAssessmentId ? 'Update Failed' : 'Deployment Failed', err.response?.data?.message || 'Check connection.');
        } finally {
            closeLoading();
        }
    };

    const handleGradeSubmit = async (e) => {
        e.preventDefault();
        showLoading('Finalizing Grade...');
        try {
            const token = localStorage.getItem('token');
            const { obtainedMarks, feedback } = selectedSubmission;
            await axios.patch(`/api/tutor/submissions/${selectedSubmission._id}/grade`, {
                obtainedMarks,
                feedback
            }, { headers: { Authorization: `Bearer ${token}` } });

            showSuccess('Score Verified', 'Student record has been officially updated.');
            setIsGradeModalOpen(false);
            fetchInitialData();
        } catch (err) {
            showError('Grading Failure', 'Synchronization error with Candidate DB.');
        } finally {
            closeLoading();
        }
    };

    const handleDeleteAssessment = async (assessment) => {
        if (!window.confirm(`Are you sure you want to decommission "${assessment.title}"?`)) return;
        showLoading('Decommissioning...');
        try {
            const token = localStorage.getItem('token');
            const courseId = assessment.courseId._id || assessment.courseId;
            const moduleId = assessment.moduleId?._id || assessment.moduleId;
            await axios.delete(`/api/tutor/courses/${courseId}/modules/${moduleId}/lessons/${assessment._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showSuccess('Decommissioned', 'Assessment removed from circulation.');
            fetchInitialData();
        } catch (err) {
            console.error(err);
            showError('Error', 'Could not delete the assessment.');
        } finally {
            closeLoading();
        }
    };

    const resetForm = () => {
        setFormData({
            courseId: '', moduleId: '', title: '', instructions: '', contentType: 'Assignment',
            maxMarks: 100, passingPercentage: 40, dueDate: '', timeLimit: 30, questions: [],
            allowedFileTypes: ['.pdf', '.docx'], mediaFile: null, status: 'Active'
        });
        setCreationStep(1);
        setEditingAssessmentId(null);
    };

    const handleEditClick = (assessment) => {
        setEditingAssessmentId(assessment._id);
        const courseId = assessment.courseId?._id || assessment.courseId;
        const moduleId = assessment.moduleId?._id || assessment.moduleId;

        setFormData({
            courseId: courseId || '',
            moduleId: moduleId || '',
            title: assessment.title || '',
            instructions: assessment.instructions || '',
            contentType: assessment.contentType || 'Assignment',
            maxMarks: assessment.maxMarks || 100,
            passingPercentage: assessment.passingPercentage || 40,
            dueDate: assessment.dueDate ? new Date(assessment.dueDate).toISOString().slice(0, 16) : '',
            timeLimit: assessment.timeLimit || 30,
            questions: assessment.questions || [],
            allowedFileTypes: assessment.allowedFileTypes || ['.pdf', '.docx'],
            mediaFile: null
        });
        setCreationStep(1);
        setIsCreateModalOpen(true);
    };

    const rosterColumns = [
        {
            header: 'Assessment Detail', key: 'title', width: '35%', render: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div className="kpi-icon-wrapper" style={{ width: '40px', height: '40px', background: row.contentType === 'Exam' ? 'rgba(212, 175, 55, 0.1)' : 'rgba(0, 44, 95, 0.1)' }}>
                        {row.contentType === 'Exam' ? <FaCheckDouble style={{ color: '#D4AF37' }} /> : <FaPenNib style={{ color: '#002C5F' }} />}
                    </div>
                    <div>
                        <strong style={{ display: 'block', fontSize: '15px' }}>{row.title}</strong>
                        <span style={{ fontSize: '12px', color: '#64748B' }}>{row.courseId?.title}</span>
                    </div>
                </div>
            )
        },
        {
            header: 'Type', key: 'contentType', width: '15%', render: (row) => (
                <span className={`status-badge ${row.contentType.toLowerCase()}`} style={{ background: row.contentType === 'Exam' ? '#FEF9C3' : '#E0F2FE', color: row.contentType === 'Exam' ? '#854D0E' : '#0369A1' }}>
                    {row.contentType}
                </span>
            )
        },
        {
            header: 'Deadline', key: 'dueDate', width: '20%', render: (row) => (
                <div style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaRegCalendarCheck style={{ color: '#64748B' }} />
                    {row.dueDate ? new Date(row.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'No Deadline'}
                </div>
            )
        },
        {
            header: 'Status', key: 'status', width: '15%', render: (row) => (
                <span className={`status-badge ${row.status?.toLowerCase() || 'active'}`} style={{
                    background: row.status === 'Inactive' ? '#FEE2E2' : (row.status === 'Archived' ? '#F1F5F9' : '#DCFCE7'),
                    color: row.status === 'Inactive' ? '#B91C1C' : (row.status === 'Archived' ? '#475569' : '#166534')
                }}>
                    {row.status || 'Active'}
                </span>
            )
        },
        {
            header: 'Control', key: 'actions', width: '15%', render: (row) => (
                <div className="action-buttons">
                    <button className="action-btn edit" title="Edit Assessment" onClick={() => handleEditClick(row)}><FaEdit /></button>
                    <button className="action-btn delete" title="Decommission" onClick={() => handleDeleteAssessment(row)}><FaTrash /></button>
                </div>
            )
        }
    ];

    const queueColumns = [
        {
            header: 'Candidate / Student', key: 'studentId', width: '30%', render: (row) => (
                <div>
                    <strong style={{ display: 'block' }}>{row.studentId.name}</strong>
                    <span style={{ fontSize: '12px', color: '#64748B' }}>{row.studentId.email}</span>
                </div>
            )
        },
        {
            header: 'Assessment Task', key: 'assessmentId', width: '30%', render: (row) => (
                <div>
                    <strong>{row.assessmentId.title}</strong>
                    <div style={{ fontSize: '12px' }}>Submitted: {new Date(row.createdAt).toLocaleString()}</div>
                </div>
            )
        },
        {
            header: 'Action Required', key: 'actions', width: '40%', render: (row) => (
                <button className="action-btn-gold" style={{ padding: '8px 16px', fontSize: '12px' }} onClick={() => { setSelectedSubmission(row); setIsGradeModalOpen(true); }}>
                    Evaluate Now <FaChevronRight style={{ marginLeft: '8px' }} />
                </button>
            )
        }
    ];

    if (loading) return <Loader />;

    const filteredAssessments = statusFilter === 'All'
        ? assessments
        : assessments.filter(item => item.status === statusFilter);

    return (
        <div className="dashboard-overview fade-in">
            {/* Header / Stats Row */}
            <div className="dashboard-header" style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                    <h1 className="page-title">Evaluations Command Center</h1>
                    <p className="page-subtitle">Architect rigorous testing frameworks, manage submissions, and verify candidate competency.</p>
                </div>
                <button className="primary-btn pulse-on-hover" onClick={() => { resetForm(); setIsCreateModalOpen(true); }}>
                    <FaPlus style={{ marginRight: '8px' }} /> Architect Evolution
                </button>
            </div>

            {/* Quick Stats Widget */}
            <div className="kpi-grid" style={{ marginBottom: '32px' }}>
                <div
                    className="kpi-card"
                    onClick={() => setActiveTab('roster')}
                    style={{
                        cursor: 'pointer',
                        border: activeTab === 'roster' ? '1px solid #D4AF37' : '',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    <div className="kpi-header">
                        <span className="kpi-title">Active Assessments</span>
                        <div className="kpi-icon-wrapper"><FaTasks /></div>
                    </div>
                    <div className="kpi-value">{assessments.length}</div>
                    <div className="kpi-trend trend-up">Current in-system evaluators</div>
                </div>
                <div
                    className="kpi-card"
                    onClick={() => setActiveTab('queue')}
                    style={{
                        cursor: 'pointer',
                        border: activeTab === 'queue' ? '1px solid #D4AF37' : '',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    <div className="kpi-header">
                        <span className="kpi-title">Grading Queue</span>
                        <div className="kpi-icon-wrapper" style={{ color: '#EF4444' }}><FaHourglassHalf /></div>
                    </div>
                    <div className="kpi-value">{pendingSubmissions.length}</div>
                    <div className="kpi-trend trend-down">Pending manual verification</div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="tabs-container" style={{ display: 'flex', gap: '2px', marginBottom: '24px', background: '#F1F5F9', padding: '4px', borderRadius: '12px', width: 'fit-content' }}>
                <button
                    onClick={() => setActiveTab('roster')}
                    style={{ padding: '10px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: '0.3s', background: activeTab === 'roster' ? 'white' : 'transparent', color: activeTab === 'roster' ? '#002C5F' : '#64748B', boxShadow: activeTab === 'roster' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none' }}>
                    <FaClipboardList style={{ marginRight: '8px' }} /> Assessment Roster
                </button>
                <button
                    onClick={() => setActiveTab('queue')}
                    style={{ padding: '10px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: '0.3s', background: activeTab === 'queue' ? 'white' : 'transparent', color: activeTab === 'queue' ? '#002C5F' : '#64748B', boxShadow: activeTab === 'queue' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none' }}>
                    <FaHourglassHalf style={{ marginRight: '8px' }} /> Grading Queue
                </button>
            </div>

            {activeTab === 'roster' ? (
                <DataTable
                    title="Assessment Infrastructure"
                    columns={rosterColumns}
                    data={filteredAssessments}
                    emptyMessage="No matching evaluations found."
                    leftControls={
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{ height: '36px', padding: '0 10px', borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '13px', cursor: 'pointer', background: '#fff', color: '#334155' }}
                        >
                            <option value="All">All Status</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                            <option value="Archived">Archived</option>
                        </select>
                    }
                />
            ) : (
                <DataTable title="Pending Student Submissions" columns={queueColumns} data={pendingSubmissions} />
            )}

            {/* --- CREATION MODAL --- */}
            {isCreateModalOpen && createPortal(
                <div className={`modal-overlay ${isCreateModalOpen ? 'open' : ''}`} onClick={() => setIsCreateModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '850px' }}>
                        <div className="modal-header">
                            <h2><FaTasks /> {editingAssessmentId ? 'Evaluation Editor' : 'Evaluation Architect'} - Step {creationStep}</h2>
                            <button className="modal-close-btn" onClick={() => setIsCreateModalOpen(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleSubmit} className="modal-form">
                                {creationStep === 1 && (
                                    <>
                                        <div className="form-row" style={{ gridColumn: 'span 2' }}>
                                            <div className="form-group">
                                                <label className="premium-label">Target Course</label>
                                                <select name="courseId" value={formData.courseId} onChange={handleFormChange} className="glass-input" required>
                                                    <option value="">-- Choose Course --</option>
                                                    {courses.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label className="premium-label">Target Module</label>
                                                <select name="moduleId" value={formData.moduleId} onChange={handleFormChange} className="glass-input" required disabled={!formData.courseId}>
                                                    <option value="">-- Choose Module --</option>
                                                    {courses.find(c => c._id === formData.courseId)?.modules.map((m, idx) => (
                                                        <option key={m._id} value={m._id}>Mod {idx + 1}: {m.moduleTitle}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                            <label className="premium-label">Evaluation Title</label>
                                            <input type="text" name="title" value={formData.title} onChange={handleFormChange} className="glass-input" placeholder="e.g. End of Module Competency Exam" required />
                                        </div>
                                        <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                            <label className="premium-label">Core Instructions for Students</label>
                                            <textarea name="instructions" value={formData.instructions} onChange={handleFormChange} className="glass-input" rows="4" placeholder="Detail the rules of this assessment..." required />
                                        </div>
                                        <div className="form-row triplet" style={{ gridColumn: 'span 2' }}>
                                            <div className="form-group">
                                                <label className="premium-label">Engine Type</label>
                                                <select name="contentType" value={formData.contentType} onChange={handleFormChange} className="glass-input">
                                                    <option value="Assignment">Practical Assignment</option>
                                                    <option value="Exam">Timed MCQ Exam</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label className="premium-label">Max Marks</label>
                                                <input type="number" name="maxMarks" value={formData.maxMarks} onChange={handleFormChange} className="glass-input" required />
                                            </div>
                                            <div className="form-group">
                                                <label className="premium-label">Passing %</label>
                                                <input type="number" name="passingPercentage" value={formData.passingPercentage} onChange={handleFormChange} className="glass-input" required />
                                            </div>
                                            <div className="form-group">
                                                <label className="premium-label">Status</label>
                                                <select name="status" value={formData.status || 'Active'} onChange={handleFormChange} className="glass-input">
                                                    <option value="Active">Active</option>
                                                    <option value="Inactive">Inactive</option>
                                                    <option value="Archived">Archived</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                            <label className="premium-label">Submission Deadline</label>
                                            <input type="datetime-local" name="dueDate" value={formData.dueDate} onChange={handleFormChange} className="glass-input" required />
                                        </div>
                                        <div className="modal-actions">
                                            <button type="button" className="action-btn-secondary" onClick={() => setIsCreateModalOpen(false)}>Discard</button>
                                            <button type="button" className="action-btn-gold" onClick={() => setCreationStep(2)}>
                                                Configure Specifics <FaChevronRight style={{ marginLeft: '10px' }} />
                                            </button>
                                        </div>
                                    </>
                                )}

                                {creationStep === 2 && formData.contentType === 'Exam' && (
                                    <>
                                        <div className="form-row" style={{ gridColumn: 'span 2' }}>
                                            <div className="form-group">
                                                <label className="premium-label">Examination Time Limit (Minutes)</label>
                                                <input type="number" name="timeLimit" value={formData.timeLimit} onChange={handleFormChange} className="glass-input" required />
                                            </div>
                                            <div className="form-group">
                                                <label className="premium-label">Question Count</label>
                                                <div className="glass-input" style={{ background: '#f1f5f9', opacity: 0.8 }}>{formData.questions.length} Items Configured</div>
                                            </div>
                                        </div>

                                        <div className="form-group full-width fade-in" style={{ padding: '24px', background: '#F8FAFC', borderRadius: '16px', border: '1px solid #E2E8F0', gridColumn: 'span 2' }}>
                                            <h4 style={{ margin: '0 0 20px 0', color: 'var(--primary-blue)', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <FaQuestionCircle /> MCQ Question Builder
                                            </h4>

                                            <div className="questions-scroll-area" style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '10px' }}>
                                                {formData.questions.map((q, qIdx) => (
                                                    <div key={qIdx} className="question-block" style={{ marginBottom: '24px', padding: '20px', border: '1px solid #E2E8F0', borderRadius: '12px', background: 'white' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                                                            <span style={{ fontWeight: '800', fontSize: '11px', color: '#64748B', letterSpacing: '0.05em' }}>QUESTION #{qIdx + 1}</span>
                                                            <button type="button" onClick={() => setFormData(p => ({ ...p, questions: p.questions.filter((_, i) => i !== qIdx) }))} style={{ border: 'none', background: '#fee2e2', color: '#ef4444', borderRadius: '6px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }}><FaTrash /></button>
                                                        </div>
                                                        <input type="text" value={q.questionText} onChange={(e) => updateQuestion(qIdx, 'questionText', e.target.value)} className="glass-input" placeholder="Question Text" style={{ marginBottom: '15px', border: '1px solid #F1F5F9' }} required />

                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '15px' }}>
                                                            {q.options.map((opt, oIdx) => (
                                                                <div key={oIdx} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                                    <span style={{ fontWeight: '700', color: '#D4AF37', minWidth: '15px' }}>{String.fromCharCode(65 + oIdx)}</span>
                                                                    <input type="text" value={opt} onChange={(e) => updateOption(qIdx, oIdx, e.target.value)} className="glass-input" placeholder={`Option ${String.fromCharCode(65 + oIdx)}`} style={{ background: '#FDFDFD' }} required />
                                                                </div>
                                                            ))}
                                                        </div>

                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '10px', borderTop: '1px solid #F1F5F9' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                <label style={{ fontSize: '12px', fontWeight: '700' }}>CORRECT KEY</label>
                                                                <select value={q.correctAnswer} onChange={(e) => updateQuestion(qIdx, 'correctAnswer', e.target.value)} className="glass-input" style={{ width: '130px', padding: '8px' }} required>
                                                                    <option value="">Select...</option>
                                                                    {q.options.map((optionValue, oIdx) => (
                                                                        <option key={oIdx} value={optionValue}>Option {String.fromCharCode(65 + oIdx)}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                <label style={{ fontSize: '12px', fontWeight: '700' }}>MARKS</label>
                                                                <input type="number" value={q.marks} onChange={(e) => updateQuestion(qIdx, 'marks', e.target.value)} className="glass-input" style={{ width: '80px', padding: '8px' }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            <button type="button" onClick={addQuestion} className="action-btn-gold" style={{ width: '100%', border: '2px dashed #D4AF37', background: 'transparent', color: '#D4AF37', marginTop: '10px' }}>
                                                <FaPlus style={{ marginRight: '8px' }} /> Add Question Layer
                                            </button>
                                        </div>

                                        <div className="modal-actions">
                                            <button type="button" className="action-btn-secondary" onClick={() => setCreationStep(1)}><FaArrowLeft /> Step 1</button>
                                            <button type="submit" className="action-btn-gold pulse-on-hover">{editingAssessmentId ? 'Update Exam Engine' : 'Deploy Exam Engine'} <FaChevronRight style={{ marginLeft: '10px' }} /></button>
                                        </div>
                                    </>
                                )}

                                {creationStep === 2 && formData.contentType === 'Assignment' && (
                                    <>
                                        <div className="form-group full-width fade-in" style={{ padding: '24px', background: '#F8FAFC', borderRadius: '16px', border: '1px solid #E2E8F0', gridColumn: 'span 2' }}>
                                            <h4 style={{ margin: '0 0 20px 0', color: 'var(--primary-blue)', fontSize: '15px' }}>Practical Resource Configuration</h4>

                                            <div className="form-group" style={{ marginBottom: '24px' }}>
                                                <label className="premium-label">Reference Material Brief (Optional)</label>
                                                <div className="upload-box" style={{ padding: '30px', border: '2px dashed #E2E8F0', borderRadius: '12px', textAlign: 'center', background: 'white' }}>
                                                    <input type="file" id="mediaFile" onChange={handleFileChange} style={{ display: 'none' }} />
                                                    <label htmlFor="mediaFile" style={{ cursor: 'pointer' }}>
                                                        <FaCloudUploadAlt style={{ fontSize: '40px', color: '#D4AF37', marginBottom: '10px' }} />
                                                        <div style={{ fontWeight: '700', color: '#002C5F' }}>{formData.mediaFile ? formData.mediaFile.name : 'Click to Upload Assignment Brief'}</div>
                                                        <div style={{ fontSize: '12px', color: '#64748B' }}>Supports PDF, Docx, or ZIP (Max 50MB)</div>
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="form-group">
                                                <label className="premium-label">Permitted Artifact Extensions</label>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', padding: '15px', background: 'white', borderRadius: '10px', border: '1px solid #F1F5F9' }}>
                                                    {['.pdf', '.docx', '.zip', '.png', '.jpg', '.mp4'].map(ext => (
                                                        <label key={ext} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={formData.allowedFileTypes.includes(ext)}
                                                                onChange={(e) => {
                                                                    const checked = e.target.checked;
                                                                    setFormData(p => ({
                                                                        ...p,
                                                                        allowedFileTypes: checked ? [...p.allowedFileTypes, ext] : p.allowedFileTypes.filter(e => e !== ext)
                                                                    }))
                                                                }}
                                                                style={{ accentColor: '#D4AF37' }}
                                                            /> {ext.toUpperCase()}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="modal-actions">
                                            <button type="button" className="action-btn-secondary" onClick={() => setCreationStep(1)}><FaArrowLeft /> Step 1</button>
                                            <button type="submit" className="action-btn-gold pulse-on-hover">{editingAssessmentId ? 'Update Assignment' : 'Dispatch Assignment'} <FaChevronRight style={{ marginLeft: '10px' }} /></button>
                                        </div>
                                    </>
                                )}
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* --- GRADING MODAL --- */}
            {isGradeModalOpen && selectedSubmission && createPortal(
                <div className={`modal-overlay ${isGradeModalOpen ? 'open' : ''}`} onClick={() => setIsGradeModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '750px' }}>
                        <div className="modal-header">
                            <h2><FaEdit /> Submission Evaluation</h2>
                            <button className="modal-close-btn" onClick={() => setIsGradeModalOpen(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <div className="candidate-info" style={{ marginBottom: '24px', padding: '20px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ fontSize: '10px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', marginBottom: '4px', letterSpacing: '0.05em' }}>Candidate Identity</div>
                                    <strong style={{ fontSize: '18px', color: '#002C5F' }}>{selectedSubmission.studentId.name}</strong>
                                    <div style={{ fontSize: '13px', color: '#64748B' }}>{selectedSubmission.studentId.email}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '10px', color: '#64748B', fontWeight: '800', textTransform: 'uppercase', marginBottom: '4px' }}>Task Type</div>
                                    <span className="status-badge" style={{ background: '#D4AF37', color: 'white', padding: '4px 12px' }}>{selectedSubmission.type}</span>
                                </div>
                            </div>

                            <form onSubmit={handleGradeSubmit} className="modal-form">
                                <div className="form-group full-width" style={{ gridColumn: 'span 2' }}>
                                    <label className="premium-label">Student Artifact / Response</label>
                                    <div className="glass-input" style={{ background: '#fff', minHeight: '80px', padding: '15px', border: '1px solid #F1F5F9' }}>
                                        {selectedSubmission.submissionFileUrl ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                <a
                                                    href={`${API_URL}${selectedSubmission.submissionFileUrl}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    style={{ color: '#D4AF37', fontWeight: '700', textDecoration: 'underline', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px' }}
                                                >
                                                    <FaCloudUploadAlt /> Open Submitted File
                                                </a>
                                                {selectedSubmission.submissionText && (
                                                    <div style={{ marginTop: '4px', fontSize: '14px', color: '#475569', background: '#f8fafc', padding: '12px', borderRadius: '8px', borderLeft: '3px solid #D4AF37' }}>
                                                        <strong>Student Notes:</strong><br />
                                                        {selectedSubmission.submissionText}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            selectedSubmission.submissionText || 'No content provided.'
                                        )}
                                    </div>
                                </div>

                                <div className="form-row" style={{ gridColumn: 'span 2' }}>
                                    <div className="form-group">
                                        <label className="premium-label">Award Marks (Max: {selectedSubmission.totalMarks})</label>
                                        <input
                                            type="number"
                                            className="glass-input"
                                            max={selectedSubmission.totalMarks}
                                            value={selectedSubmission.obtainedMarks || 0}
                                            onChange={(e) => setSelectedSubmission({ ...selectedSubmission, obtainedMarks: Number(e.target.value) })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="premium-label">Current Percentage</label>
                                        <div className="glass-input" style={{ background: '#f1f5f9', fontWeight: '800', color: '#002C5F' }}>
                                            {((selectedSubmission.obtainedMarks || 0) / selectedSubmission.totalMarks * 100).toFixed(1)}% Score
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group full-width" style={{ gridColumn: 'span 2' }}>
                                    <label className="premium-label">Technical Feedback & Observations</label>
                                    <textarea
                                        className="glass-input"
                                        rows="5"
                                        placeholder="Detail the candidate's performance, strengths, and areas for improvement..."
                                        value={selectedSubmission.feedback || ''}
                                        onChange={(e) => setSelectedSubmission({ ...selectedSubmission, feedback: e.target.value })}
                                        required
                                        style={{ background: 'white' }}
                                    />
                                </div>

                                <div className="modal-actions" style={{ marginTop: '24px' }}>
                                    <button type="button" className="action-btn-secondary" onClick={() => setIsGradeModalOpen(false)}>Discard</button>
                                    <button type="submit" className="action-btn-gold pulse-on-hover">Authorize Grade & Sync to DB</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div >,
                document.body
            )}
        </div >
    );
};

export default TutorAssignments;
